import React from 'react';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const BufferCapacityEvaluation = ({ bufferData, onChange, errors }) => {
  const handleInputChange = (field, value) => {
    onChange({ ...bufferData, [field]: value });
  };

  const calculateBufferDays = () => {
    const capacity = parseFloat(bufferData?.storageCapacity) || 0;
    const daily = parseFloat(bufferData?.dailyConsumption) || 0;
    if (daily === 0) return 0;
    return (capacity / daily)?.toFixed(1);
  };

  const calculateResilienceScore = () => {
    const days = parseFloat(calculateBufferDays());
    if (days >= 7) return { score: 95, label: 'Sangat Baik', color: 'text-success' };
    if (days >= 5) return { score: 80, label: 'Baik', color: 'text-success' };
    if (days >= 3) return { score: 60, label: 'Cukup', color: 'text-warning' };
    if (days >= 1) return { score: 40, label: 'Rendah', color: 'text-error' };
    return { score: 20, label: 'Kritis', color: 'text-destructive' };
  };

  const resilienceScore = calculateResilienceScore();

  return (
    <div className="card space-y-6">
      <div className="flex items-center gap-3 pb-4 border-b border-border">
        <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center">
          <Icon name="Shield" size={24} color="var(--color-success)" />
        </div>
        <div>
          <h2 className="text-xl font-semibold">Evaluasi Buffer & Resiliensi</h2>
          <p className="text-sm text-muted-foreground">Kapasitas penyangga untuk menghadapi gangguan supply</p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
        <Input
          label="Kapasitas Penyimpanan (Liter)"
          type="number"
          placeholder="Contoh: 30000"
          value={bufferData?.storageCapacity || ''}
          onChange={(e) => handleInputChange('storageCapacity', e?.target?.value)}
          error={errors?.storageCapacity}
          required
          description="Total kapasitas cold storage dan tangki"
        />

        <Input
          label="Konsumsi Harian (Liter)"
          type="number"
          placeholder="Contoh: 5000"
          value={bufferData?.dailyConsumption || ''}
          onChange={(e) => handleInputChange('dailyConsumption', e?.target?.value)}
          error={errors?.dailyConsumption}
          required
          description="Rata-rata penggunaan susu per hari"
        />

        <Input
          label="Stok Saat Ini (Liter)"
          type="number"
          placeholder="Contoh: 20000"
          value={bufferData?.currentStock || ''}
          onChange={(e) => handleInputChange('currentStock', e?.target?.value)}
          error={errors?.currentStock}
          required
          description="Volume stok susu saat ini"
        />

        <Input
          label="Waktu Restock (Hari)"
          type="number"
          placeholder="Contoh: 2"
          value={bufferData?.restockTime || ''}
          onChange={(e) => handleInputChange('restockTime', e?.target?.value)}
          error={errors?.restockTime}
          description="Waktu yang dibutuhkan untuk restock"
        />

        <Input
          label="Jumlah Supplier Cadangan"
          type="number"
          placeholder="Contoh: 3"
          value={bufferData?.backupSuppliers || ''}
          onChange={(e) => handleInputChange('backupSuppliers', e?.target?.value)}
          description="Supplier alternatif yang tersedia"
        />

        <Input
          label="Kapasitas Cooling (Liter)"
          type="number"
          placeholder="Contoh: 35000"
          value={bufferData?.coolingCapacity || ''}
          onChange={(e) => handleInputChange('coolingCapacity', e?.target?.value)}
          description="Kapasitas pendinginan total"
        />
      </div>
      {bufferData?.storageCapacity && bufferData?.dailyConsumption && (
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">Buffer Days</p>
                <Icon name="Calendar" size={20} color="var(--color-primary)" />
              </div>
              <p className="text-3xl font-bold text-primary data-text">{calculateBufferDays()}</p>
              <p className="text-xs text-muted-foreground mt-1">Hari cadangan operasional</p>
            </div>

            <div className="p-4 rounded-lg bg-muted border border-border">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">Skor Resiliensi</p>
                <Icon name="TrendingUp" size={20} color={`var(--color-${resilienceScore?.color?.replace('text-', '')})`} />
              </div>
              <p className={`text-3xl font-bold ${resilienceScore?.color} data-text`}>{resilienceScore?.score}</p>
              <p className="text-xs text-muted-foreground mt-1">{resilienceScore?.label}</p>
            </div>

            <div className="p-4 rounded-lg bg-muted border border-border">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">Utilisasi Storage</p>
                <Icon name="Database" size={20} color="var(--color-accent)" />
              </div>
              <p className="text-3xl font-bold text-accent data-text">
                {((parseFloat(bufferData?.currentStock) / parseFloat(bufferData?.storageCapacity)) * 100)?.toFixed(0)}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">Kapasitas terpakai</p>
            </div>
          </div>

          {parseFloat(calculateBufferDays()) < 3 && (
            <div className="p-4 rounded-lg bg-warning/10 border border-warning/20 flex items-start gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-warning">Peringatan: Buffer Capacity Rendah</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Buffer kurang dari 3 hari meningkatkan risiko gangguan operasional. Pertimbangkan untuk meningkatkan kapasitas penyimpanan atau diversifikasi supplier.
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BufferCapacityEvaluation;